import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../context/SettingsContext';
import { createEvent } from '../api';
import type { Vehicle, EventType, VatStatus } from '../types';
import { displayToKm, distanceUnitLabel, displayToLiters, volumeUnitLabel } from '../utils/units';
import PriceInput from './PriceInput';
import ExpenseModal from './ExpenseModal';
import UserSelect from './UserSelect';
import QuickEndTime from './QuickEndTime';

// Typerne man kan vælge i første trin. Vedligehold er delt i to, fordi planlagt og
// uplanlagt er samme begivenhed med hver sin formular.
export type EventChoice = 'checkup' | 'maintenance_unplanned' | 'maintenance_planned' | 'inspection' | 'expense';

interface Props {
  vehicle: Vehicle;
  onSaved: () => void;
  onClose: () => void;
  // Spring type-valget over. Bruges fra /expenses, hvor valget allerede er truffet.
  initialChoice?: EventChoice;
}

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// Konvertér brugerinput ("1.820,29" eller "1820.29") til tal/streng til backend
const parseAmountNullable = (v: string): number | null => {
  if (!v) return null;
  const n = parseFloat(v.replace(/\./g, '').replace(',', '.'));
  return isFinite(n) ? n : null;
};
const parseAmountStr = (v: string): string | null => {
  const n = parseAmountNullable(v);
  return n != null ? String(n) : null;
};

function calcMinutes(start: string, end: string): string {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? String(diff) : '';
}

function addYears(date: string, years: number): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  return `${y + years}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export default function EventModal({ vehicle, onSaved, onClose, initialChoice }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [step, setStep] = useState<'choose' | EventType | 'expense'>(
    initialChoice ? (initialChoice.startsWith('maintenance') ? 'maintenance' : initialChoice as EventType | 'expense') : 'choose'
  );
  const [saving, setSaving] = useState(false);

  // Inspection form
  type InsType = 'periodic' | 'recheck' | 'registration';
  type InsResult = 'passed' | 'conditional' | 'failed';

  const calcPeriodicNextDue = (fromDate: string): string => {
    const firstReg = vehicle.first_registered_on;
    if (firstReg) {
      const ageYears = (new Date(fromDate).getTime() - new Date(firstReg).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      if (ageYears < settings.firstInspectionAge) {
        return addYears(firstReg, settings.firstInspectionAge);
      }
    }
    return addYears(fromDate, settings.inspectionInterval);
  };

  const [insType, setInsType] = useState<InsType>('periodic');
  const [insDate, setInsDate] = useState(today());
  const [insBlocked, setInsBlocked] = useState(false);
  const [insStartTime, setInsStartTime] = useState('');
  const [insEndTime, setInsEndTime] = useState('');
  const [insPerformedBy, setInsPerformedBy] = useState('');
  const [insKm, setInsKm] = useState('');
  const [insResult, setInsResult] = useState<InsResult>('passed');
  const [insCost, setInsCost] = useState('');
  const [insVatStatus, setInsVatStatus] = useState<VatStatus>('full');
  const [insVatAmount, setInsVatAmount] = useState('');
  const [insTime, setInsTime] = useState('');
  const [insNotes, setInsNotes] = useState('');
  const [insNextDue, setInsNextDue] = useState(
    vehicle.next_inspection ?? calcPeriodicNextDue(today())
  );

  const handleInsDateChange = (val: string) => {
    setInsDate(val);
    if (insType === 'periodic' && insResult === 'passed') {
      setInsNextDue(calcPeriodicNextDue(val));
    }
  };

  const handleInsTypeChange = (val: InsType) => {
    setInsType(val);
    if (val === 'periodic') {
      setInsNextDue(insResult === 'passed' ? calcPeriodicNextDue(insDate) : '');
    } else {
      setInsNextDue(vehicle.next_inspection ?? '');
    }
  };

  const handleInsResultChange = (val: InsResult) => {
    setInsResult(val);
    if (insType === 'periodic') {
      setInsNextDue(val === 'passed' ? calcPeriodicNextDue(insDate) : '');
    }
  };

  // Maintenance form
  const [mType, setMType] = useState<'planned' | 'unplanned'>(
    initialChoice === 'maintenance_unplanned' ? 'unplanned' : 'planned'
  );
  const [mDate, setMDate] = useState(today());
  const [mKm, setMKm] = useState('');
  const [mCost, setMCost] = useState('');
  const [mVatStatus, setMVatStatus] = useState<VatStatus>('full');
  const [mVatAmount, setMVatAmount] = useState('');
  const [mTime, setMTime] = useState('');
  const [mNextDate, setMNextDate] = useState(addYears(today(), 1));
  const [mNextKm, setMNextKm] = useState('');
  const [mNotes, setMNotes] = useState('');
  // Værksted (unplanned) extras
  const [mStartTime, setMStartTime] = useState('');
  const [mEndTime, setMEndTime] = useState('');
  const [mPerformedBy, setMPerformedBy] = useState('');
  const [mBlocked, setMBlocked] = useState(false);
  const [mOfflineFrom, setMOfflineFrom] = useState('');
  const [mOfflineTo, setMOfflineTo] = useState('');

  // Checkup form
  const [cDate, setCDate] = useState(today());
  const [cBlocked, setCBlocked] = useState(false);
  const [cStartTime, setCStartTime] = useState('');
  const [cEndTime, setCEndTime] = useState('');
  const [cBy, setCBy] = useState('');
  // Der skal altid vælges en medarbejder (Kennith 2026-10-14) — advarsel ved gem uden
  const [employeeMissing, setEmployeeMissing] = useState(false);
  const [cWashed, setCWashed] = useState(true);
  const [cVacuumed, setCVacuumed] = useState(true);
  const [cCleaned, setCCleaned] = useState(true);
  const [cExtCond, setCExtCond] = useState<number | ''>('');
  const [cIntCond, setCIntCond] = useState<number | ''>('');
  const [cTyre, setCTyre] = useState(false);
  const [cWasherFluid, setCWasherFluid] = useState(false);
  const [cWasherFill, setCWasherFill] = useState(false);
  const [cOilChecked, setCOilChecked] = useState(false);
  const [cOilAdded, setCOilAdded] = useState(false);
  const [cWindowClean, setCWindowClean] = useState(false);
  const [cOilLitres, setCOilLitres] = useState('');
  const [cAdblue, setCAdblue] = useState(false);
  const [cAdblueKm, setCAdblueKm] = useState('');
  const [cControlOk, setCControlOk] = useState(false);
  const [cKm, setCKm] = useState('');
  const [cTime, setCTime] = useState('');
  const [cNotes, setCNotes] = useState('');

  // "Kontrolleret, alt OK": ryd øvrige afkrydsninger og sæt tidsforbrug til 5 min
  const applyControlOk = (checked: boolean) => {
    setCControlOk(checked);
    if (checked) {
      setCWashed(false); setCVacuumed(false); setCCleaned(false); setCWindowClean(false);
      setCTyre(false); setCWasherFluid(false); setCWasherFill(false);
      setCOilChecked(false); setCOilAdded(false); setCAdblue(false);
      setCOilLitres(''); setCAdblueKm('');
      setCExtCond(''); setCIntCond('');
      setCTime('5');
    }
  };

  const distUnit = settings.distanceUnit;
  const distLabel = distanceUnitLabel(distUnit);
  const toKm = (val: string) => {
    if (!val) return null;
    // Fjern tusindtals-separatorer (punktum i DK, komma i US) før konvertering
    const cleaned = val.replace(/\./g, '').replace(/,/g, '');
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? null : displayToKm(num, distUnit);
  };
  const fuelUnit = settings.fuelUnit;
  const fuelLabel = volumeUnitLabel(fuelUnit);
  const toLiters = (val: string) => val ? displayToLiters(+val, fuelUnit) : null;

  const handleSave = async () => {
    const employee = step === 'inspection' ? insPerformedBy : step === 'maintenance' ? mPerformedBy : step === 'checkup' ? cBy : 'n/a';
    if (!employee) { setEmployeeMissing(true); return; }
    setSaving(true);
    if (step === 'inspection') {
      await createEvent('inspection', {
        vehicle_id: vehicle.id, date: insDate,
        blocked_for_rental: insBlocked,
        start_time: insStartTime || null, end_time: insEndTime || null,
        performed_by_user_id: Number(insPerformedBy) || null, km_reading: toKm(insKm),
        result: insResult, inspection_type: insType,
        notes: insNotes || null,
        cost: parseAmountStr(insCost), vat_status: insVatStatus, vat_amount: parseAmountStr(insVatAmount),
        time_spent: insTime || null,
        next_due_date: insType === 'periodic' ? (insNextDue || null) : null,
      });
    } else if (step === 'maintenance') {
      const mOffline = {
        offline_from: mOfflineFrom ? mOfflineFrom.replace('T', ' ') : null,
        offline_to:   mOfflineTo   ? mOfflineTo.replace('T', ' ')   : null,
      };
      await createEvent('maintenance', mType === 'unplanned' ? {
        vehicle_id: vehicle.id, type: 'unplanned',
        date: mDate, start_time: mStartTime || null, end_time: mEndTime || null,
        performed_by_user_id: Number(mPerformedBy) || null, time_spent: mTime || null,
        cost: parseAmountStr(mCost), vat_status: mVatStatus, vat_amount: parseAmountStr(mVatAmount),
        notes: mNotes || null, blocked_for_rental: mBlocked, ...mOffline,
      } : {
        vehicle_id: vehicle.id, type: 'planned',
        date: mDate, start_time: mStartTime || null, end_time: mEndTime || null,
        blocked_for_rental: mBlocked, km_reading: toKm(mKm),
        cost: parseAmountStr(mCost), vat_status: mVatStatus, vat_amount: parseAmountStr(mVatAmount),
        performed_by_user_id: Number(mPerformedBy) || null, time_spent: mTime || null,
        next_due_date: mNextDate || null, next_due_km: toKm(mNextKm), notes: mNotes || null, ...mOffline,
      });
    } else if (step === 'checkup') {
      await createEvent('checkup', {
        vehicle_id: vehicle.id, date: cDate,
        start_time: cStartTime || null, end_time: cEndTime || null,
        blocked_for_rental: cBlocked, performed_by_user_id: Number(cBy) || null,
        washed: cWashed, vacuumed: cVacuumed, cleaned: cCleaned,
        exterior_condition: cExtCond || null, interior_condition: cIntCond || null,
        tyre_pressure: cTyre, washer_fluid: cWasherFluid, washer_fill: cWasherFill,
        oil_checked: cOilChecked, oil_added: cOilAdded, oil_added_litres: toLiters(cOilLitres),
        window_clean: cWindowClean, adblue_fill: cAdblue,
        adblue_fill_km: cAdblue ? toKm(cAdblueKm) : null, control_ok: cControlOk,
        km_reading: toKm(cKm), time_spent: cTime || null, notes: cNotes || null,
      });
    }
    setSaving(false);
    onSaved();
  };

  const stepTitle = () => {
    if (step === 'choose') return t('events.choose_type');
    if (step === 'expense') return t('expense.title');
    if (step === 'inspection') return t('events.inspection_register');
    if (step === 'maintenance') return t(`events.${mType === 'planned' ? 'planned' : 'unplanned'}`);
    return t(`events.${step}`);
  };

  // "Anden udgift" bruger den rigtige udgiftsformular frem for en indbygget kopi.
  // Den havde hverken bilagsscanning, vedhæftede bilag eller tilbagevendende udgift, så
  // samme handling gav to forskellige formularer alt efter hvor man startede.
  // Bilen er valgt på forhånd, men kan skiftes — og ryddes, hvis udgiften gælder hele flåden.
  if (step === 'expense') {
    return (
      <ExpenseModal
        expense={{ vehicle_id: vehicle.id ?? null, registration_number: vehicle.registration_number }}
        onSaved={onSaved}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-lg">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b sticky top-0 bg-[var(--color-surface)] z-10">
          <h2 className="modal-title text-base sm:text-lg">
            {vehicle.registration_number} — {stepTitle()}
          </h2>
          <button onClick={onClose} className="modal-close p-2 -mr-2">&times;</button>
        </div>

        <div className="p-4 sm:p-6">

          {/* Step 1: choose type */}
          {step === 'choose' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([
                { key: 'checkup', label: t('events.checkup'), color: 'border-purple-200 hover:bg-purple-50' },
                { key: 'maintenance_unplanned', label: t('events.unplanned'), color: 'border-orange-200 hover:bg-orange-50' },
                { key: 'maintenance_planned', label: t('events.planned'), color: 'border-green-200 hover:bg-[var(--color-success-100)]' },
                { key: 'inspection', label: t('events.inspection'), color: 'border-[var(--color-border)] hover:bg-[var(--color-primary-soft-bg)]' },
                { key: 'expense', label: t('expense.title'), color: 'border-yellow-200 hover:bg-[var(--color-warning-100)]' },
              ] as const).map(opt => (
                <button key={opt.key}
                  onClick={() => {
                    if (opt.key === 'maintenance_planned') { setMType('planned'); setStep('maintenance'); }
                    else if (opt.key === 'maintenance_unplanned') { setMType('unplanned'); setStep('maintenance'); }
                    else setStep(opt.key as EventType | 'expense');
                  }}
                  className={`border-2 rounded-lg p-4 text-base sm:text-sm font-medium text-gray-700 text-left transition-colors min-h-[56px] sm:min-h-0 ${opt.color}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Inspection form */}
          {step === 'inspection' && (
            <div className="space-y-4">
              {/* Række 1: Dato + Blokeret for udlejning */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.date')}</label>
                  <input type="date" className="input" value={insDate} onChange={e => handleInsDateChange(e.target.value)} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={insBlocked} onChange={e => setInsBlocked(e.target.checked)}
                      className="rounded border-gray-300 text-[var(--color-primary)]" />
                    {t('events.blocked_for_rental')}
                  </label>
                </div>
              </div>
              {/* Række 2: Synstype + Kilometerstand */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.inspection_type')}</label>
                  <select className="input" value={insType} onChange={e => handleInsTypeChange(e.target.value as 'periodic' | 'recheck' | 'registration')}>
                    <option value="periodic">{t('events.inspection_type_periodic')}</option>
                    <option value="recheck">{t('events.inspection_type_recheck')}</option>
                    <option value="registration">{t('events.inspection_type_registration')}</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('events.km_reading')} ({distLabel})</label>
                  <input type="number" className="input" value={insKm} onChange={e => setInsKm(e.target.value)} />
                </div>
              </div>
              {/* Række 3: Resultat + Næste syn */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.result')}</label>
                  <select className="input" value={insResult} onChange={e => handleInsResultChange(e.target.value as 'passed' | 'conditional' | 'failed')}>
                    <option value="passed">{t('events.passed')}</option>
                    <option value="conditional">{t('events.conditional')}</option>
                    <option value="failed">{t('events.failed')}</option>
                  </select>
                </div>
                {insType === 'periodic' ? (
                  <div>
                    <label className="label">{t('events.next_due_date')}</label>
                    <input type="date" className="input" value={insNextDue} onChange={e => setInsNextDue(e.target.value)} />
                  </div>
                ) : <div />}
              </div>
              {/* Række 4: Bemærkninger */}
              <div>
                <label className="label">{t('events.notes')}</label>
                <textarea className="input" rows={2} value={insNotes} onChange={e => setInsNotes(e.target.value)} />
              </div>
              {/* Række 5: Pris */}
              <PriceInput
                label={t('events.cost')}
                amount={insCost}
                vatStatus={insVatStatus}
                vatAmount={insVatAmount}
                onAmountChange={setInsCost}
                onVatStatusChange={setInsVatStatus}
                onVatAmountChange={setInsVatAmount}
                vatRate={settings.vatRate}
              />
              {/* Række 6: Start tid + Slut tid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.start_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={insStartTime}
                      onChange={e => { setInsStartTime(e.target.value); setInsTime(calcMinutes(e.target.value, insEndTime)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setInsStartTime(v); setInsTime(calcMinutes(v, insEndTime)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                </div>
                <div>
                  <label className="label">{t('events.end_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={insEndTime}
                      onChange={e => { setInsEndTime(e.target.value); setInsTime(calcMinutes(insStartTime, e.target.value)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setInsEndTime(v); setInsTime(calcMinutes(insStartTime, v)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                  <QuickEndTime start={insStartTime} onPick={(v, rest) => { setInsEndTime(v); if (!rest) setInsTime(calcMinutes(insStartTime, v)); }} />
                </div>
              </div>
              {/* Række 7: Medarbejder + Tid brugt */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.performed_by')}</label>
                  <UserSelect value={insPerformedBy} onChange={setInsPerformedBy} defaultName={settings.defaultUser} missing={employeeMissing} />
                </div>
                <div>
                  <label className="label">{t('events.time_spent')}</label>
                  <input type="number" className="input" value={insTime} onChange={e => setInsTime(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Maintenance form — Værksted (unplanned) */}
          {step === 'maintenance' && mType === 'unplanned' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.date')}</label>
                  <input type="date" className="input" value={mDate} onChange={e => setMDate(e.target.value)} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={mBlocked} onChange={e => setMBlocked(e.target.checked)}
                      className="rounded border-gray-300 text-[var(--color-primary)]" />
                    {t('events.blocked_for_rental')}
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.start_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={mStartTime}
                      onChange={e => { setMStartTime(e.target.value); setMTime(calcMinutes(e.target.value, mEndTime)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setMStartTime(v); setMTime(calcMinutes(v, mEndTime)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                </div>
                <div>
                  <label className="label">{t('events.end_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={mEndTime}
                      onChange={e => { setMEndTime(e.target.value); setMTime(calcMinutes(mStartTime, e.target.value)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setMEndTime(v); setMTime(calcMinutes(mStartTime, v)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                  <QuickEndTime start={mStartTime} onPick={(v, rest) => { setMEndTime(v); if (!rest) setMTime(calcMinutes(mStartTime, v)); }} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.delivered_by')}</label>
                  <UserSelect value={mPerformedBy} onChange={setMPerformedBy} defaultName={settings.defaultUser} missing={employeeMissing} />
                </div>
                <div>
                  <label className="label">{t('events.time_spent')}</label>
                  <input type="number" className="input" value={mTime} onChange={e => setMTime(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="label">{t('events.work_performed')}</label>
                <textarea className="input" rows={3} value={mNotes} onChange={e => setMNotes(e.target.value)} />
              </div>
              <PriceInput
                label={t('events.cost')}
                amount={mCost}
                vatStatus={mVatStatus}
                vatAmount={mVatAmount}
                onAmountChange={setMCost}
                onVatStatusChange={setMVatStatus}
                onVatAmountChange={setMVatAmount}
                vatRate={settings.vatRate}
              />
              <div className="border-t pt-3">
                <label className="label mb-2">{t('events.offline_duration')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label text-xs text-gray-400">{t('events.offline_from')}</label>
                    <input type="datetime-local" value={mOfflineFrom} onChange={e => setMOfflineFrom(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-cream-100 focus:outline-none focus:border-gray-300 w-full" />
                  </div>
                  <div>
                    <label className="label text-xs text-gray-400">{t('events.offline_to')}</label>
                    <input type="datetime-local" value={mOfflineTo} min={mOfflineFrom || undefined} onChange={e => setMOfflineTo(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-cream-100 focus:outline-none focus:border-gray-300 w-full" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance form — Planlagt serviceeftersyn (planned) */}
          {step === 'maintenance' && mType === 'planned' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.date')}</label>
                  <input type="date" className="input" value={mDate} onChange={e => { setMDate(e.target.value); setMNextDate(addYears(e.target.value, 1)); }} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={mBlocked} onChange={e => setMBlocked(e.target.checked)}
                      className="rounded border-gray-300 text-[var(--color-primary)]" />
                    {t('events.blocked_for_rental')}
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.start_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={mStartTime}
                      onChange={e => { setMStartTime(e.target.value); setMTime(calcMinutes(e.target.value, mEndTime)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setMStartTime(v); setMTime(calcMinutes(v, mEndTime)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                </div>
                <div>
                  <label className="label">{t('events.end_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={mEndTime}
                      onChange={e => { setMEndTime(e.target.value); setMTime(calcMinutes(mStartTime, e.target.value)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setMEndTime(v); setMTime(calcMinutes(mStartTime, v)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                  <QuickEndTime start={mStartTime} onPick={(v, rest) => { setMEndTime(v); if (!rest) setMTime(calcMinutes(mStartTime, v)); }} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.km_reading')} ({distLabel})</label>
                  <input type="number" className="input" value={mKm} onChange={e => setMKm(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="label">{t('events.notes')}</label>
                <textarea className="input" rows={2} value={mNotes} onChange={e => setMNotes(e.target.value)} />
              </div>
              <PriceInput
                label={t('events.cost')}
                amount={mCost}
                vatStatus={mVatStatus}
                vatAmount={mVatAmount}
                onAmountChange={setMCost}
                onVatStatusChange={setMVatStatus}
                onVatAmountChange={setMVatAmount}
                vatRate={settings.vatRate}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.next_due_date_service')}</label>
                  <input type="date" className="input" value={mNextDate} onChange={e => setMNextDate(e.target.value)} />
                </div>
                <div>
                  <label className="label">{t('events.next_due_km')} ({distLabel})</label>
                  <input type="number" className="input" value={mNextKm} onChange={e => setMNextKm(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.performed_by')}</label>
                  <UserSelect value={mPerformedBy} onChange={setMPerformedBy} defaultName={settings.defaultUser} missing={employeeMissing} />
                </div>
                <div>
                  <label className="label">{t('events.time_spent')}</label>
                  <input type="number" className="input" value={mTime} onChange={e => setMTime(e.target.value)} />
                </div>
              </div>
              <div className="border-t pt-3">
                <label className="label mb-2">{t('events.offline_duration')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label text-xs text-gray-400">{t('events.offline_from')}</label>
                    <input type="datetime-local" value={mOfflineFrom} onChange={e => setMOfflineFrom(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-cream-100 focus:outline-none focus:border-gray-300 w-full" />
                  </div>
                  <div>
                    <label className="label text-xs text-gray-400">{t('events.offline_to')}</label>
                    <input type="datetime-local" value={mOfflineTo} min={mOfflineFrom || undefined} onChange={e => setMOfflineTo(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-cream-100 focus:outline-none focus:border-gray-300 w-full" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Checkup form */}
          {step === 'checkup' && (
            <div className="space-y-4">
              {/* Række 1: Dato + Blokeret */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.date')}</label>
                  <input type="date" className="input" value={cDate} onChange={e => setCDate(e.target.value)} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={cBlocked} onChange={e => setCBlocked(e.target.checked)}
                      className="rounded border-gray-300 text-[var(--color-primary)]" />
                    {t('events.blocked_for_rental')}
                  </label>
                </div>
              </div>
              {/* Række 2: Start tid + Slut tid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.start_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={cStartTime}
                      onChange={e => { setCStartTime(e.target.value); setCTime(calcMinutes(e.target.value, cEndTime)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setCStartTime(v); setCTime(calcMinutes(v, cEndTime)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                </div>
                <div>
                  <label className="label">{t('events.end_time')}</label>
                  <div className="flex gap-2">
                    <input type="time" className="input" value={cEndTime}
                      onChange={e => { setCEndTime(e.target.value); setCTime(calcMinutes(cStartTime, e.target.value)); }}
                      onWheel={e => e.currentTarget.blur()} />
                    <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setCEndTime(v); setCTime(calcMinutes(cStartTime, v)); }}
                      className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
                  </div>
                  <QuickEndTime start={cStartTime} onPick={(v, rest) => { setCEndTime(v); if (!rest) setCTime(calcMinutes(cStartTime, v)); }} />
                </div>
              </div>

              {/* Række 2: Afkrydsningsfelter */}
              <div className="grid grid-cols-2 gap-2">
                {([
                  { key: 'washed',       val: cWashed,      set: setCWashed },
                  { key: 'vacuumed',     val: cVacuumed,    set: setCVacuumed },
                  { key: 'cleaned',      val: cCleaned,     set: setCCleaned },
                  { key: 'window_clean', val: cWindowClean, set: setCWindowClean },
                  { key: 'tyre_pressure',val: cTyre,        set: setCTyre },
                  { key: 'washer_fluid', val: cWasherFluid, set: setCWasherFluid },
                  { key: 'washer_fill',  val: cWasherFill,  set: setCWasherFill },
                  { key: 'oil_checked',  val: cOilChecked,  set: setCOilChecked },
                  { key: 'oil_added',    val: cOilAdded,    set: setCOilAdded },
                  { key: 'adblue_fill',  val: cAdblue,      set: setCAdblue },
                ] as const).map(item => (
                  <label key={item.key} className="checkbox-label">
                    <input type="checkbox" checked={item.val}
                      onChange={e => { item.set(e.target.checked); if (e.target.checked) setCControlOk(false); }}
                      className="rounded border-gray-300 text-[var(--color-primary)]" />
                    {t(`events.${item.key}`)}
                  </label>
                ))}
              </div>

              {/* Stand-vurdering (1-10) — bruges til statistik over rengoerings-interval */}
              <div className="border-t pt-3 space-y-2">
                <p className="label">Bilens stand (1-10)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Udvendigt</span>
                      <span className="text-sm font-medium" style={{ minWidth: 24 }}>{cExtCond || '—'}</span>
                    </div>
                    <input type="range" min={1} max={10} step={1}
                      value={cExtCond || 5}
                      onChange={e => setCExtCond(parseInt(e.target.value, 10))}
                      className="w-full" />
                    {!cExtCond && (
                      <button type="button" onClick={() => setCExtCond(5)} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                        Klik for at vurdere
                      </button>
                    )}
                    {!!cExtCond && (
                      <button type="button" onClick={() => setCExtCond('')} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                        Ryd
                      </button>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Indvendigt</span>
                      <span className="text-sm font-medium" style={{ minWidth: 24 }}>{cIntCond || '—'}</span>
                    </div>
                    <input type="range" min={1} max={10} step={1}
                      value={cIntCond || 5}
                      onChange={e => setCIntCond(parseInt(e.target.value, 10))}
                      className="w-full" />
                    {!cIntCond && (
                      <button type="button" onClick={() => setCIntCond(5)} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                        Klik for at vurdere
                      </button>
                    )}
                    {!!cIntCond && (
                      <button type="button" onClick={() => setCIntCond('')} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                        Ryd
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-400">1 = meget snavset/skadet, 10 = som ny. Lad være med at vurdere hvis ikke relevant.</p>
              </div>

              {/* Hurtig kontrol — fjerner øvrige valg og sætter tidsforbrug til 5 min */}
              <label className="checkbox-label font-medium border-t pt-3">
                <input type="checkbox" checked={cControlOk} onChange={e => applyControlOk(e.target.checked)}
                  className="rounded border-gray-300 text-[var(--color-primary)]" />
                {t('events.control_ok')}
              </label>

              {/* Række 3 (kun ved olie): Påfyldt + Km stand */}
              {cOilAdded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('events.oil_added_litres')} ({fuelLabel})</label>
                    <input type="number" step="0.1" className="input" value={cOilLitres} onChange={e => setCOilLitres(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">{t('events.km_reading_oil')} ({distLabel})</label>
                    <input type="number" className="input" value={cKm} onChange={e => setCKm(e.target.value)} />
                  </div>
                </div>
              )}

              {cAdblue && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('events.adblue_fill_km')} ({distLabel})</label>
                    <input type="number" className="input" value={cAdblueKm} onChange={e => setCAdblueKm(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Række 4: Medarbejder | Tid brugt */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.performed_by')}</label>
                  <UserSelect value={cBy} onChange={setCBy} defaultName={settings.defaultUser} missing={employeeMissing} />
                </div>
                <div>
                  <label className="label">{t('events.time_spent')}</label>
                  <input type="number" className="input" value={cTime} onChange={e => setCTime(e.target.value)} />
                </div>
              </div>

              {/* Række 5: Noter */}
              <div>
                <label className="label">{t('events.notes')}</label>
                <textarea className="input" rows={2} value={cNotes} onChange={e => setCNotes(e.target.value)} />
              </div>
            </div>
          )}

          {/* Expense form */}
        </div>

        {/* Footer */}
        {step !== 'choose' && (
          <div className="flex items-center justify-end px-6 pb-6 pt-2 gap-3">
            <button onClick={onClose} className="btn btn-secondary">
              {t('modal.cancel')}
            </button>
            <button onClick={handleSave} disabled={saving} className="btn btn-primary">
              {saving ? '...' : t('modal.save')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
